
import { useQuery } from "@tanstack/react-query"
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { UseFormReturn } from "react-hook-form"
import { RegistrationFormData } from "@/types/auth"
import { useState, useEffect } from "react"

interface LocationFieldsProps {
  form: UseFormReturn<RegistrationFormData>
}

export function LocationFields({ form }: LocationFieldsProps) {
  const [selectedState, setSelectedState] = useState<string>("")
  
  // Fetch states from IBGE API
  const { data: states } = useQuery({
    queryKey: ["states"],
    queryFn: async () => {
      const response = await fetch(
        "https://servicodados.ibge.gov.br/api/v1/localidades/estados"
      )
      const data = await response.json()
      return data.sort((a: any, b: any) => a.nome.localeCompare(b.nome))
    },
  })

  // Fetch cities based on selected state
  const { data: cities } = useQuery({
    queryKey: ["cities", selectedState],
    queryFn: async () => {
      if (!selectedState) return []
      const state = states?.find((s: any) => s.sigla === selectedState)
      if (!state) return []

      const response = await fetch(
        `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${state.id}/municipios`
      )
      const data = await response.json()
      return data.sort((a: any, b: any) => a.nome.localeCompare(b.nome))
    },
    enabled: !!selectedState && !!states,
  })

  // Update selectedState when form's state value changes
  useEffect(() => {
    const stateValue = form.watch("state")
    if (stateValue) {
      setSelectedState(stateValue)
    }
  }, [form.watch("state")])

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <FormField
        control={form.control}
        name="state"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-sm font-medium text-left block">
              Estado
            </FormLabel>
            <Select 
              onValueChange={(value) => {
                field.onChange(value)
                setSelectedState(value)
                // Reset city when state changes
                form.setValue("city", "")
              }}
              value={field.value || ""}
            >
              <FormControl>
                <SelectTrigger className="h-11">
                  <SelectValue placeholder="Selecione um estado" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {states?.map((state: any) => (
                  <SelectItem key={state.id} value={state.sigla}>
                    {state.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="city"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-sm font-medium text-left block">
              Cidade
            </FormLabel>
            <Select 
              onValueChange={field.onChange}
              value={field.value || ""}
              disabled={!selectedState}
            >
              <FormControl>
                <SelectTrigger className="h-11">
                  <SelectValue placeholder="Selecione uma cidade" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {cities?.map((city: any) => (
                  <SelectItem key={city.id} value={city.nome}>
                    {city.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  )
}
